import { supabase } from './supabase';

export type SubscriptionPlan = 'trial' | 'basic' | 'standard' | 'premium' | 'expired';

export interface PlanFeatures {
  maxStudents: number;
  aiTutoring: boolean;
  homeworkManagement: boolean;
  educationalGames: boolean;
  weeklyReports: boolean;
  emailSupport: boolean;
  smartStudyPlan: boolean;
  practiceTests: boolean;
  familyConnect: boolean;
  prioritySupport: boolean;
  calendar: boolean;
  liveSessions: boolean;
}

// Función para normalizar nombres de planes (case-insensitive y manejo de alias)
export const normalizePlan = (planName: string | null | undefined): SubscriptionPlan => {
  if (!planName) return 'trial';

  const normalized = planName.toLowerCase().trim();

  if (normalized === 'basic' || normalized === 'basico') return 'basic';
  if (normalized === 'standard' || normalized === 'estandar' || normalized === 'family') return 'standard';
  if (normalized === 'premium') return 'premium';
  if (normalized === 'trial' || normalized === 'prueba') return 'trial';
  if (normalized === 'expired' || normalized === 'expirado') return 'expired';
  if (normalized === 'cancelled' || normalized === 'cancelado') return 'expired'; // Or map to a specific cancelled state if needed

  return 'trial'; // Fallback
};

// Definir características por plan
export const PLAN_FEATURES: Record<SubscriptionPlan, PlanFeatures> = {
  expired: {
    maxStudents: 0,
    aiTutoring: false,
    homeworkManagement: false,
    educationalGames: false,
    weeklyReports: false,
    emailSupport: false,
    smartStudyPlan: false,
    practiceTests: false,
    familyConnect: false,
    prioritySupport: false,
    calendar: false,
    liveSessions: false,
  },
  trial: {
    maxStudents: 1,
    aiTutoring: true,
    homeworkManagement: true,
    educationalGames: true,
    weeklyReports: true,
    emailSupport: true,
    smartStudyPlan: true,
    practiceTests: true,
    familyConnect: true,
    prioritySupport: true,
    calendar: true,
    liveSessions: true,
  },
  basic: {
    maxStudents: 1,
    aiTutoring: true,
    homeworkManagement: true,
    educationalGames: true,
    weeklyReports: true,
    emailSupport: true,
    smartStudyPlan: false,
    practiceTests: false,
    familyConnect: false,
    prioritySupport: false,
    calendar: false,
    liveSessions: true, // Habilitado para permitir las 4 sesiones/mes configuradas
  },
  standard: {
    maxStudents: 3, // Actualizado de 2 a 3 según imagen
    aiTutoring: true,
    homeworkManagement: true,
    educationalGames: true,
    weeklyReports: true,
    emailSupport: true,
    smartStudyPlan: true,
    practiceTests: true,
    familyConnect: true,
    prioritySupport: true,
    calendar: true,
    liveSessions: true,
  },
  premium: {
    maxStudents: Infinity,
    aiTutoring: true,
    homeworkManagement: true,
    educationalGames: true,
    weeklyReports: true,
    emailSupport: true,
    smartStudyPlan: true,
    practiceTests: true,
    familyConnect: true,
    prioritySupport: true,
    calendar: true,
    liveSessions: true,
  },
};

// Obtener el plan actual del usuario
export const getUserPlan = async (): Promise<SubscriptionPlan> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      return 'trial';
    }

    const { data: profile } = await supabase
      .from('parent_profiles')
      .select('subscription_plan, trial_end_date, created_at')
      .eq('id', user.id)
      .single();

    if (!profile) {
      return 'trial';
    }

    // Normalizar plan usando la nueva función centralizada
    const subscriptionPlan = normalizePlan(profile.subscription_plan);

    // Verificar si el trial ha expirado
    if (subscriptionPlan === 'trial') {
      const trialEndDate = profile.trial_end_date
        ? new Date(profile.trial_end_date)
        : new Date(new Date(profile.created_at).getTime() + 3 * 24 * 60 * 60 * 1000);

      const now = new Date();

      if (now > trialEndDate) {
        return 'expired';
      }
    }

    return subscriptionPlan;
  } catch (error) {
    console.error('Error obteniendo plan del usuario:', error);
    return 'trial';
  }
};

// Verificar si el usuario tiene acceso a una característica
export const hasFeatureAccess = async (feature: keyof PlanFeatures): Promise<boolean> => {
  const plan = await getUserPlan();
  return PLAN_FEATURES[plan][feature] as unknown as boolean;
};

// Alias para mantener compatibilidad con componentes que usan el nombre anterior
export const hasAccessToFeature = hasFeatureAccess;

// Obtener características del plan actual
export const getCurrentPlanFeatures = async (): Promise<PlanFeatures> => {
  const plan = await getUserPlan();
  return PLAN_FEATURES[plan];
};

// Verificar si el usuario puede agregar más estudiantes
export const canAddMoreStudents = async (currentStudentCount: number): Promise<boolean> => {
  const plan = await getUserPlan();
  const maxStudents = PLAN_FEATURES[plan].maxStudents;
  return currentStudentCount < maxStudents;
};

// Obtener nombre del plan en español
export const getPlanDisplayName = (plan: SubscriptionPlan): string => {
  const names: Record<SubscriptionPlan, string> = {
    trial: 'Prueba Gratuita',
    basic: 'Plan Basic',
    standard: 'Plan Familiar',
    premium: 'Plan Premium',
    expired: 'Plan Expirado (Renovar)',
  };
  return names[plan];
};

// Rutas bloqueadas por plan
export const RESTRICTED_ROUTES: Record<string, keyof PlanFeatures> = {
  '/smart-study-plan': 'smartStudyPlan',
  '/simulacros': 'practiceTests',
  '/calendar': 'calendar',
};

// Verificar si el usuario puede acceder a una ruta
export const canAccessRoute = async (route: string): Promise<boolean> => {
  const feature = RESTRICTED_ROUTES[route];

  if (!feature) {
    // Si la ruta no está restringida, permitir acceso
    return true;
  }

  return await hasFeatureAccess(feature);
};